import projectRaw from '@data/project.json';
import unitsRaw from '@data/units.json';
import warrantiesRaw from '@data/warranties.json';
import masterplanRaw from '@data/masterplan.json';
import {
  API_ROUTES,
  ManifestSchema,
  MasterplanSchema,
  ProjectSchema,
  UnitsResponseSchema,
  UnitsSchema,
  WarrantiesSchema,
  type Manifest,
  type Masterplan,
  type Project,
  type Unit,
  type Warranties,
} from '@alrimal/contracts';

/**
 * Where the data comes from.
 *
 * The bundled JSON is the source of truth for everything that does not change
 * without a deploy — names, rooms, warranties, the site plan. Status and price
 * *do* change without a deploy, so they are merged in from the API when it
 * answers, and the page renders fully before that ever happens.
 *
 * That ordering is deliberate. A buyer on a slow connection sees the whole
 * project immediately rather than a spinner waiting on a row of two columns.
 */

export interface StaticData {
  project: Project;
  units: Unit[];
  warranties: Warranties;
  masterplan: Masterplan;
}

/**
 * Validated at module load. A malformed edit to `data/*.json` fails on the
 * first render with a precise path rather than as `undefined.map is not a
 * function` three components deep.
 */
export const staticData: StaticData = {
  project: ProjectSchema.parse(projectRaw),
  units: UnitsSchema.parse(unitsRaw),
  warranties: WarrantiesSchema.parse(warrantiesRaw),
  masterplan: MasterplanSchema.parse(masterplanRaw),
};

/**
 * Live status and price.
 *
 * Returns `null` on any failure — no API deployed, offline, a 500. The caller
 * keeps the bundled values, which are correct for everything except
 * availability, and availability degrades to "call us", which is the outcome
 * the whole page is trying to produce anyway.
 */
export async function fetchLiveUnits(
  signal?: AbortSignal,
): Promise<Unit[] | null> {
  try {
    const response = await fetch(API_ROUTES.units, {
      signal: signal ?? null,
      headers: { accept: 'application/json' },
    });
    if (!response.ok) return null;
    const parsed = UnitsResponseSchema.safeParse(await response.json());
    return parsed.success ? parsed.data.units : null;
  } catch {
    return null;
  }
}

/** The asset manifest, written by whichever tier produced the geometry. */
export async function fetchManifest(
  basePath = '/assets/manifest.json',
  signal?: AbortSignal,
): Promise<Manifest> {
  const response = await fetch(basePath, { signal: signal ?? null });
  if (!response.ok) {
    throw new Error(`Asset manifest is unavailable (${response.status})`);
  }
  return ManifestSchema.parse(await response.json());
}

/**
 * Where a unit's panorama lives.
 *
 * Panoramas are stills outside the manifest (§13): one equirectangular image
 * per `panoramaSet`, served as a static asset and fetched only when a buyer
 * opens the tour. JPEG, because a photographic equirect at PNG size would be
 * megabytes a phone pays for on tap. Real captures replace the file; nothing
 * else changes.
 */
export function panoramaUrl(panoramaSet: string): string {
  return `/assets/panoramas/${panoramaSet}.jpg`;
}

/**
 * Merge live rows onto the bundled catalogue.
 *
 * Only `status` and `price` are taken from the API. Everything else stays
 * bundled, so a partial or stale API response can never blank out a room list.
 */
export function mergeLiveUnits(base: readonly Unit[], live: readonly Unit[]): Unit[] {
  const liveById = new Map<string, { status: Unit['dwellings'][number]['status']; price: number | null }>();
  for (const unit of live) {
    for (const dwelling of unit.dwellings) {
      liveById.set(dwelling.id, {
        status: dwelling.status,
        price: dwelling.price,
      });
    }
  }

  return base.map((unit) => ({
    ...unit,
    dwellings: unit.dwellings.map((dwelling) => {
      const update = liveById.get(dwelling.id);
      return update
        ? { ...dwelling, status: update.status, price: update.price }
        : dwelling;
    }),
  }));
}
